<template>
  <div class="mb-4 flex justify-end py-2">
    <LinkToCreate :to="{ name: 'categoryCreate' }">
      Add Category
    </LinkToCreate>
  </div>

  <BaseCard
    class="flex size-full grow flex-col overflow-hidden"
  >
    <QueryLoading v-if="isLoading" class="flex-1" />
    <QueryError
      v-else-if="isError"
      :show-retry="true"
      class="m-auto"
      @retry="refetch"
    >
      Failed to load categories
    </QueryError>
    <template v-else>
      <div class="flex justify-between">
        <SearchInput
          v-model="filters.search"
          class="mb-4 w-full max-w-md shrink-0"
        />
      </div>
      <div class="h-full overflow-y-auto">
        <!-- @vue-generic {ListCategoriesResponseItem, ColumnId}-->
        <BaseTable :rows="categories" :columns="columns">
          <template #cell-color="{ value }">
            <ColorItem :value="value" rounded />
          </template>

          <template #cell-name="{ row }">
            <div class="w-full">
              <p
                class="mr-2 line-clamp-2 text-sm font-semibold"
              >
                {{ row.namePath }}
              </p>
            </div>
          </template>

          <template #cell-actions="{ row }">
            <div
              class="flex items-center justify-end space-x-2"
            >
              <BaseTableButton
                variant="info"
                :to="{
                  name: 'categoryEdit',
                  params: { categoryId: row.id },
                }"
              >
                Edit
              </BaseTableButton>
              <BaseTableButton
                variant="danger"
                @click="onDeleteCategory(row.id)"
              >
                Delete
              </BaseTableButton>
            </div>
          </template>

          <template #grid-card="{ row }">
            <div
              class="flex w-full items-center space-x-2 border-l-2 pl-2"
              :style="{
                borderColor: row.color ?? undefined,
              }"
            >
              <div class="flex-1">
                <p
                  class="mr-2 line-clamp-2 text-sm font-semibold"
                >
                  {{ row.namePath }}
                </p>
              </div>

              <ButtonWithIcon
                @click="onClickThreeDots(row.id)"
              >
                <IconVerticalEllipsis />
              </ButtonWithIcon>
            </div>
          </template>
        </BaseTable>
      </div>
    </template>
  </BaseCard>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    confirm-label="Delete Category"
    title="Are you sure you wish to delete this category?"
    description=" This will delete all child categories and products.
      This action cannot be undone."
    cancel-label="No"
  />

  <BaseDialog
    :is-open="isActionsDialogOpen"
    @close="onActionsDialogCancel"
  >
    <ul class="flex flex-col space-y-2 p-2">
      <li class="relative cursor-default select-none">
        <button
          :class="[
            'block w-full truncate py-2 capitalize',
            'hover:bg-background-fg/3',
          ]"
          @click="onActionsDialogConfirm('edit')"
        >
          Edit
        </button>
      </li>

      <li class="relative cursor-default select-none">
        <button
          :class="[
            'block w-full truncate py-2 capitalize',
            'hover:bg-background-fg/3',
          ]"
          @click="onActionsDialogConfirm('delete')"
        >
          Delete
        </button>
      </li>
    </ul>
  </BaseDialog>
</template>

<script setup lang="ts">
import ColorItem from "@/components/ColorItem.vue"
import type { TableColumn } from "@/components/BaseTable.vue"
const router = useRouter()
const filters = reactive({
  search: "",
})
const {
  data: categories,
  isError,
  refetch,
  isLoading,
} = useCategories(filters)

type ColumnId = "color" | "name" | "actions"

const columns = ref<
  Array<TableColumn<ListCategoriesResponseItem, ColumnId>>
>([
  {
    id: "color",
    header: "",
    getCellValue: (row) => row.color,
    autoWidth: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) => row.name,
  },
  {
    id: "actions",
    autoWidth: true,
  },
])

const deleteConfirmDialogRef = useTemplateRef(
  "deleteConfirmDialogRef"
)
const onDeleteCategory = async (categoryId: string) => {
  if (deleteConfirmDialogRef.value === null) return
  const { isCanceled: isDeleteCancelled } =
    await deleteConfirmDialogRef.value.reveal()
  if (isDeleteCancelled) return

  try {
    await deleteCategory(categoryId)
    toast.success("Category deleted")
  } catch (error) {
    toast.error("Failed to delete category")
    logError(error)
  }
}

const {
  reveal: revealActionsDialog,
  isRevealed: isActionsDialogOpen,
  cancel: onActionsDialogCancel,
  confirm: onActionsDialogConfirm,
} = useConfirmDialog<undefined, "edit" | "delete">()

const onClickThreeDots = async (categoryId: string) => {
  const { data, isCanceled } = await revealActionsDialog()
  if (isCanceled) return
  if (data === "edit") {
    router.push({
      name: "categoryEdit",
      params: {
        categoryId: categoryId,
      },
    })
  } else if (data === "delete") {
    onDeleteCategory(categoryId)
  } else {
    return
  }
}
</script>
